import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface ExportOptions {
  title: string;
  headers: string[];
  rows: string[][];
  filename: string;
  subtitle?: string;
}

export function exportToPdf({ title, headers, rows, filename, subtitle }: ExportOptions) {
  const doc = new jsPDF({ orientation: rows[0]?.length > 5 ? "landscape" : "portrait" });

  // Brand header
  doc.setFontSize(18);
  doc.setTextColor(30, 30, 30);
  doc.text(title, 14, 20);

  if (subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(subtitle, 14, 27);
  }

  doc.setFontSize(8);
  doc.setTextColor(160, 160, 160);
  const genLine = `Report Generated: ${new Date().toLocaleString()} | Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
  doc.text(genLine, 14, subtitle ? 33 : 27);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: subtitle ? 38 : 32,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 30, 30], textColor: [255, 215, 0], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 14, right: 14 },
  });

  // Footer with timestamp on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text(`SecureFlex · Page ${i} of ${pageCount} · Printed: ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.height - 10);
  }

  doc.save(`${filename}.pdf`);
}

export function exportSchedulePdf(data: any[]) {
  exportToPdf({
    title: "PATROL SCHEDULE REPORT",
    subtitle: `Total: ${data.length} entries · Date range included`,
    headers: ["Date", "Due Time", "Checkpoint", "Site", "Officer", "Status", "Actual Time", "Actual Date", "Variance"],
    rows: data.map(s => [
      s.dueDate || "—",
      s.due,
      s.checkpoint,
      s.site,
      s.officer,
      s.status?.toUpperCase(),
      s.actual || "—",
      s.actualDate || "—",
      s.variance || "—",
    ]),
    filename: `schedule-report-${new Date().toISOString().slice(0, 10)}`,
  });
}

export function exportLogPdf(data: any[]) {
  exportToPdf({
    title: "CHECK-IN LOG REPORT",
    subtitle: `Total: ${data.length} check-ins · All timestamps in local time`,
    headers: ["Date", "Time", "Checkpoint", "Site", "Officer", "Method", "Scheduled", "Status", "Variance"],
    rows: data.map(l => [
      l.date || "—",
      l.time,
      l.checkpoint,
      l.site,
      l.officer,
      l.method,
      l.scheduled || "—",
      l.onTime ? "On Time" : "Late",
      l.variance || "—",
    ]),
    filename: `checkin-log-${new Date().toISOString().slice(0, 10)}`,
  });
}

export function exportIncidentsPdf(data: any[]) {
  exportToPdf({
    title: "INCIDENTS REPORT",
    subtitle: `Total: ${data.length} incidents · Full timestamps included`,
    headers: ["Date", "Time", "Title", "Severity", "Category", "Officer", "Site", "Escalated", "Resolved"],
    rows: data.map(i => [
      i.date,
      i.time,
      i.title,
      i.severity,
      i.category,
      i.officer,
      i.site,
      i.escalated ? "Yes" : "No",
      i.resolved ? "Yes" : "No",
    ]),
    filename: `incidents-report-${new Date().toISOString().slice(0, 10)}`,
  });
}

export function exportRisksPdf(data: any[]) {
  exportToPdf({
    title: "RISK ASSESSMENT REPORT",
    subtitle: `Total: ${data.length} risks`,
    headers: ["Title", "Category", "Severity", "Status", "Likelihood", "Impact", "Score", "Mitigation Status", "Created"],
    rows: data.map(r => [
      r.title,
      r.category,
      r.severity,
      r.status,
      String(r.likelihood),
      String(r.impact),
      String(r.risk_score ?? r.likelihood * r.impact),
      r.mitigation_status,
      r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : "—",
    ]),
    filename: `risk-report-${new Date().toISOString().slice(0, 10)}`,
  });
}

export function exportFaceLogPdf(data: any[]) {
  exportToPdf({
    title: "FACE VERIFICATION LOG",
    subtitle: `Total: ${data.length} verifications · Full timestamps included`,
    headers: ["Date", "Time", "Officer", "Match", "Confidence", "Reason", "Location"],
    rows: data.map(f => {
      const d = new Date(f.verified_at);
      return [
        d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        f.officer_name,
        f.match ? "MATCH" : "NO MATCH",
        `${f.confidence}%`,
        f.reason || "—",
        f.lat && f.lng ? `${f.lat.toFixed(4)}, ${f.lng.toFixed(4)}` : "—",
      ];
    }),
    filename: `face-verification-log-${new Date().toISOString().slice(0, 10)}`,
  });
}

interface TimesheetExportOptions {
  officerName: string;
  dateFrom: string;
  dateTo: string;
  shifts: Array<{
    date: string;
    clockIn: string;
    clockOut: string;
    totalHours: string;
    site: string;
    faceConfidence: string;
    status: string;
  }>;
  totalHours: number;
}

export function exportTimesheetPdf({ officerName, dateFrom, dateTo, shifts, totalHours }: TimesheetExportOptions) {
  const doc = new jsPDF();

  // Brand header
  doc.setFontSize(18);
  doc.setTextColor(30, 30, 30);
  doc.text("OFFICER TIMESHEET", 14, 20);

  doc.setFontSize(11);
  doc.setTextColor(80, 80, 80);
  doc.text(`Officer: ${officerName}`, 14, 28);

  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`Period: ${dateFrom} to ${dateTo}`, 14, 34);
  doc.text(`Total Hours: ${totalHours.toFixed(2)}h`, 14, 39);

  doc.setFontSize(8);
  doc.setTextColor(160, 160, 160);
  const genLine = `Report Generated: ${new Date().toLocaleString()} | Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
  doc.text(genLine, 14, 45);

  autoTable(doc, {
    head: [["Date", "Clock In", "Clock Out", "Hours", "Site", "Face Match", "Status"]],
    body: shifts.map(s => [s.date, s.clockIn, s.clockOut, s.totalHours, s.site, s.faceConfidence, s.status]),
    startY: 50,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 30, 30], textColor: [255, 215, 0], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 14, right: 14 },
  });

  // Summary row
  const finalY = (doc as any).lastAutoTable?.finalY || 120;
  doc.setFillColor(30, 30, 30);
  doc.rect(14, finalY + 2, doc.internal.pageSize.width - 28, 10, "F");
  doc.setFontSize(9);
  doc.setTextColor(255, 215, 0);
  doc.text(`TOTAL HOURS: ${totalHours.toFixed(2)}h`, 18, finalY + 8.5);
  doc.text(`SHIFTS: ${shifts.length}`, 90, finalY + 8.5);

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text(`SecureFlex · Timesheet · ${officerName} · Page ${i} of ${pageCount} · Printed: ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.height - 10);
  }

  doc.save(`timesheet-${officerName.replace(/\s+/g, "-").toLowerCase()}-${dateFrom}-to-${dateTo}.pdf`);
}
